/*
 * treat-free-gift.js — Treat free gifts in the cart (2026-10-11; mystery gift and single-pass sync 2026-11-02).
 *
 * One script runs every gift rule from ONE cart snapshot, so the rules never fight over the cart:
 *
 *   1. Mystery gift (window.treatMysteryGift) — for every unit of the trigger product in the cart
 *      (KIT / THE ESSENTIALS), keeps one mystery gift line. Each new gift is picked at random from
 *      the mystery products that are in stock; gifts already in the cart are never re-shuffled.
 *      Fewer kits → the extra gifts come out.
 *   2. Threshold gift (window.treatGift) — keeps the gift product (the tote) at exactly 1 while the
 *      cart (after discounts, without ANY gift line) is at or above the threshold.
 *
 * Speed: one snapshot → at most one /cart/update.js (removals) and one /cart/add.js (additions),
 * each returning the re-rendered cart sections, which are swapped in directly — no extra fetches.
 *
 * Security: this script only tidies the cart and never prices anything. The mystery gift is free
 * through its automatic "Buy X get Y" discount in Shopify admin (re-checked by Shopify at
 * checkout). The tote's price is set in admin; orders that should not carry it are caught after
 * checkout by a Shopify Flow workflow.
 *
 * Config comes from layout/theme.liquid:
 *   window.treatGift = { variantId, threshold, available }
 *   window.treatMysteryGift = { triggerProductId, variants: [{ id, available }] }
 */
(function () {
  const gift = window.treatGift && window.treatGift.variantId ? window.treatGift : null;
  const mystery =
    window.treatMysteryGift &&
    window.treatMysteryGift.triggerProductId &&
    Array.isArray(window.treatMysteryGift.variants) &&
    window.treatMysteryGift.variants.length
      ? window.treatMysteryGift
      : null;
  if (!gift && !mystery) return;

  const mysteryIds = new Set(mystery ? mystery.variants.map((variant) => variant.id) : []);
  /* Mystery variants Shopify refused this visit (sold out since the page loaded). */
  const refused = new Set();

  const isThresholdGift = (item) => gift && item.variant_id === gift.variantId;
  const isMysteryGift = (item) => mysteryIds.has(item.variant_id);
  const isAnyGift = (item) => isThresholdGift(item) || isMysteryGift(item);

  const rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1;
  const thresholdCents = gift ? Math.round(gift.threshold * rate) : 0;
  let busy = false;
  let again = false;

  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };

  async function getCart() {
    const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  /* The cart sections on this page, rendered by Shopify in the same response as the change. */
  function sectionIds() {
    const ids = [];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (items && items.dataset.id) ids.push(items.dataset.id);
    if (footer && footer.dataset.id) ids.push(footer.dataset.id);
    ids.push('cart-icon-bubble');
    return ids;
  }

  async function post(path, payload) {
    const response = await fetch(`${cartUrl}/${path}`, {
      method: 'POST',
      headers: json,
      body: JSON.stringify(Object.assign({ sections: sectionIds(), sections_url: window.location.pathname }, payload)),
    });
    if (!response.ok) return null;
    return response.json();
  }

  const randomMystery = () => {
    const candidates = mystery.variants.filter((variant) => variant.available && !refused.has(variant.id));
    return candidates.length ? candidates[Math.floor(Math.random() * candidates.length)].id : null;
  };

  /* Works out every change both rules need from one cart snapshot. */
  function plan(cart) {
    const updates = {};
    const adds = [];

    if (mystery) {
      const target = cart.items
        .filter((item) => item.product_id === mystery.triggerProductId)
        .reduce((sum, item) => sum + item.quantity, 0);
      const lines = cart.items.filter(isMysteryGift);
      const current = lines.reduce((sum, item) => sum + item.quantity, 0);
      if (current > target) {
        /* Take the extras off the newest lines first. */
        let excess = current - target;
        for (let i = lines.length - 1; i >= 0 && excess > 0; i--) {
          const take = Math.min(lines[i].quantity, excess);
          updates[lines[i].key] = lines[i].quantity - take;
          excess -= take;
        }
      }
      for (let n = current; n < target; n++) {
        const id = randomMystery();
        if (!id) break;
        const same = adds.find((add) => add.id === id);
        if (same) same.quantity += 1;
        else adds.push({ id, quantity: 1 });
      }
    }

    if (gift) {
      const lines = cart.items.filter(isThresholdGift);
      const quantity = lines.reduce((sum, item) => sum + item.quantity, 0);
      /* No gift line of any rule counts toward the amount. */
      const giftValue = cart.items.filter(isAnyGift).reduce((sum, item) => sum + item.final_line_price, 0);
      const qualifies = cart.items.some((item) => !isAnyGift(item)) && cart.total_price - giftValue >= thresholdCents;
      /* Already one in the cart but just sold out: keep it, it is reserved for this order. */
      const target = qualifies && (gift.available || quantity >= 1) ? 1 : 0;
      if (target === 0) {
        lines.forEach((line) => (updates[line.key] = 0));
      } else if (quantity === 0) {
        adds.push({ id: gift.variantId, quantity: 1 });
      } else if (quantity !== 1 || lines.length > 1) {
        lines.forEach((line, index) => (updates[line.key] = index === 0 ? 1 : 0));
      }
    }

    return { updates, adds };
  }

  /* Swap in the sections Shopify rendered with the change. */
  function render(sections) {
    if (!sections) return;
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

    if (sections['cart-drawer']) {
      const html = parse(sections['cart-drawer']);
      ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
        const target = document.querySelector(selector);
        const source = html.querySelector(selector);
        if (target && source) target.replaceWith(source);
      });
      const inner = document.querySelector('cart-drawer .drawer__inner');
      const sourceInner = html.querySelector('.drawer__inner');
      if (inner && sourceInner) inner.classList.toggle('is-empty', sourceInner.classList.contains('is-empty'));
    }

    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    [items, footer].forEach((wrapper) => {
      if (!wrapper || !sections[wrapper.dataset.id]) return;
      const source = parse(sections[wrapper.dataset.id]).querySelector('.js-contents');
      const destination = wrapper.querySelector('.js-contents');
      if (source && destination) destination.innerHTML = source.innerHTML;
    });
    if (items) {
      const empty = !document.querySelector('#main-cart-items .cart-item');
      document.querySelector('cart-items')?.classList.toggle('is-empty', empty);
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const source = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }
  }

  /* Slow path, only when Shopify refuses a batch (a gift sold out since the page loaded): add the
     items one at a time, swapping a refused mystery flavour for another in-stock one. */
  async function addOneByOne(adds) {
    let last = null;
    for (const add of adds) {
      for (let unit = 0; unit < add.quantity; unit++) {
        let id = add.id;
        while (id) {
          const result = await post('add.js', { items: [{ id, quantity: 1 }] });
          if (result) {
            last = result;
            break;
          }
          if (!mysteryIds.has(id)) break;
          refused.add(id);
          id = randomMystery();
        }
      }
    }
    return last;
  }

  /* Sense refreshes the drawer itself on the same cart event; if that slower, older render lands
     after ours, the gift lines would vanish from view. Re-apply ours if the line count drifts. */
  let guardTimers = [];
  function guardDrawer(sections) {
    guardTimers.forEach(clearTimeout);
    guardTimers = [];
    if (!sections || !sections['cart-drawer']) return;
    const expected = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelectorAll('cart-drawer-items .cart-item').length;
    [500, 1200].forEach((delay) => {
      guardTimers.push(
        setTimeout(() => {
          if (busy) return;
          if (document.querySelector('cart-drawer-items .loading__spinner:not(.hidden), .cart__items--disabled')) return;
          const shown = document.querySelectorAll('cart-drawer-items .cart-item').length;
          if (shown !== expected) render({ 'cart-drawer': sections['cart-drawer'] });
        }, delay)
      );
    });
  }

  let running = Promise.resolve();

  /* knownCart: the cart JSON a theme cart change already returned, so no extra request. */
  function sync(knownCart) {
    if (busy) {
      again = true;
      return running;
    }
    running = run(knownCart);
    return running;
  }

  async function run(knownCart) {
    busy = true;
    try {
      const cart = knownCart && Array.isArray(knownCart.items) ? knownCart : await getCart();
      const { updates, adds } = plan(cart);
      let result = null;
      if (Object.keys(updates).length) result = (await post('update.js', { updates })) || result;
      if (adds.length) result = (await post('add.js', { items: adds })) || (await addOneByOne(adds)) || result;
      if (result) {
        render(result.sections);
        guardDrawer(result.sections);
      }
    } catch (error) {
      /* Network hiccup: the next cart change tries again. */
    } finally {
      busy = false;
      if (again) {
        again = false;
        await run();
      }
    }
  }

  /* The theme greys the line list while a quantity change or removal is on its way to Shopify;
     wait for that to land (at most 4 s) so the check sees the cart the shopper sees. */
  function waitForCartIdle() {
    return new Promise((resolve) => {
      const startedAt = Date.now();
      const tick = () => {
        const updating = document.querySelector('.cart__items--disabled, cart-items .loading__spinner:not(.hidden), cart-drawer-items .loading__spinner:not(.hidden)');
        if (!updating || Date.now() - startedAt > 4000) resolve();
        else setTimeout(tick, 60);
      };
      setTimeout(tick, 150);
    });
  }

  function start() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'treat-gift') return;
        guardTimers.forEach(clearTimeout);
        sync(event && event.cartData);
      });
    }
    /* Checkout waits for the gift check: a shopper who removes a product and taps Checkout at
       once must not reach checkout with a gift the cart no longer earns. The check runs, then
       the browser goes to checkout. */
    document.addEventListener(
      'submit',
      async (event) => {
        const submitter = event.submitter;
        if (!submitter || submitter.name !== 'checkout') return;
        event.preventDefault();
        submitter.setAttribute('aria-busy', 'true');
        submitter.disabled = true;
        try {
          await waitForCartIdle();
          await sync();
          await running;
        } finally {
          window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'checkout';
        }
      },
      true
    );

    /* "Buy it now" (Shopify's dynamic checkout button) goes straight to checkout and skips the
       cart, so the gifts would never be added. Take the click over: add the product to the cart
       the same way the Add to cart button does, run the gift check, then go to checkout. */
    let buyingNow = false;
    document.addEventListener(
      'click',
      async (event) => {
        const path = event.composedPath();
        const button = path.find(
          (node) =>
            node instanceof Element &&
            (node.matches('.shopify-payment-button__button, shopify-buy-it-now-button') || node.tagName === 'SHOPIFY-BUY-IT-NOW-BUTTON')
        );
        if (!button) return;
        const wrapper = path.find((node) => node instanceof Element && node.classList && node.classList.contains('shopify-payment-button'));
        const form = (wrapper || button).closest('form[action*="/cart/add"]');
        if (!form) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (buyingNow) return;
        buyingNow = true;
        button.setAttribute('aria-busy', 'true');

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        try {
          const formData = new FormData(form);
          /* Same rule as product-form.js: an empty gift message is not sent. */
          document.querySelectorAll(`.treat-gift-message__input[form="${form.id}"]`).forEach((field) => {
            if (field.name && field.value.trim() === '') formData.delete(field.name);
          });
          const added = await fetch(`${cartUrl}/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          if (!added.ok) {
            /* Sold out or over the stock limit: show the cart, where Shopify's message is visible. */
            window.location.href = cartUrl;
            return;
          }
          await sync();
          await running;
          window.location.href = root + 'checkout';
        } catch (error) {
          window.location.href = cartUrl;
        }
      },
      true
    );

    /* Covers carts changed elsewhere (another tab, a link, the back button). */
    sync();
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) sync();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
